import { Invoice } from './paymentService';
import api from './api';
import { apiRoot } from './apiInstances';

class InvoiceService {
  async getAllInvoices(): Promise<Invoice[]> {
    const response = await api.get(`/invoices`);
    return response.data;
  }

  async getInvoiceById(id: number): Promise<Invoice> {
    const response = await api.get(`/invoices/${id}`);
    return response.data;
  }

  async getInvoiceByNumber(invoiceNumber: string): Promise<Invoice> {
    const response = await api.get(`/invoices/number/${invoiceNumber}`);
    return response.data;
  }

  async getInvoiceByWorkOrderId(workOrderId: number): Promise<Invoice | null> {
    try {
      const response = await api.get(`/invoices/work-order/${workOrderId}`);
      return response.data;
    } catch (error: any) {
      // 404: no invoice for this work order yet.
      // 403: the reader is a technician, and invoices are owner/advisor only.
      // The work-order screen asks for this on every open regardless of role,
      // so a technician's refusal threw an unhandled rejection — the CRA error
      // overlay covered their own job. Neither case is an error to report:
      // there is simply no invoice to show them.
      if (error.response?.status === 404 || error.response?.status === 403) {
        return null;
      }
      throw error;
    }
  }

  async archiveInvoice(id: number): Promise<void> {
    await api.post(`/invoices/${id}/archive`, {});
  }

  /**
   * Persist editable invoice fields (notes, internal_notes, tax-exemption override).
   * Maps to PUT /api/admin/invoices/:id. Returns the updated invoice.
   */
  async updateInvoice(
    id: number,
    payload: Partial<{
      notes: string | null;
      internal_notes: string | null;
      is_tax_exempt: boolean;
      tax_exempt_reason: string | null;
    }>
  ): Promise<Invoice> {
    const response = await api.put(
      `/invoices/${id}`,
      payload
    );
    return response.data;
  }

  /**
   * Mint a customer-facing portal share link for an invoice and (optionally)
   * deliver it via email/SMS. Maps to POST /api/invoices/:id/share-link, which
   * is mounted at the API root (not under /admin). The backend returns a portal
   * token; callers build the portal URL from it.
   */
  async createShareLink(
    invoiceId: number,
    method: 'email' | 'sms' = 'email'
  ): Promise<{ token: string; sent_via: string }> {
    const response = await apiRoot.post(
      `/invoices/${invoiceId}/share-link`,
      { method }
    );
    return response.data;
  }

  async sendInvoiceEmail(params: {
    invoiceId: number;
    pdfBuffer: string; // base64 encoded PDF
    customerEmail: string;
    customerName?: string;
  }): Promise<{ success: boolean; message: string }> {
    const response = await api.post(
      `/invoices/${params.invoiceId}/send`,
      {
        pdfBuffer: params.pdfBuffer,
        customerEmail: params.customerEmail,
        customerName: params.customerName,
      }
    );
    return response.data;
  }
}

export const invoiceService = new InvoiceService();
